import { Dimensions } from './hooks/useWindowDimensions';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { animated, config, useSpring } from '@react-spring/web';
import { createUseGesture, dragAction } from '@use-gesture/react';
import useSound from 'use-sound';
import rotateSfx from './sounds/rotate.mp3';
import takeSfx from './sounds/take.mp3';
import dropSfx from './sounds/drop.mp3';
import insertSfx from './sounds/insert.mp3';

type PieceProps = {
	i: number;
	id: number;
	pieceWidth: number;
	pieceHeight: number;
	overlap: number;
	puzzleSize: { x: number; y: number };
	windowDim: Dimensions;
	image: string;
	zIndexMax: MutableRefObject<number>;
	fillCell: (i: number, correct: boolean) => boolean;
	emptyCell: (i: number) => void;
	onLoadImage: (id: number) => void;
	onLoadError: () => void;
	visible: boolean;
	enabled: boolean;
};
const Piece = ({
	i,
	id,
	pieceWidth,
	pieceHeight,
	overlap,
	puzzleSize,
	windowDim,
	image,
	zIndexMax,
	fillCell,
	emptyCell,
	onLoadImage,
	onLoadError,
	visible,
	enabled,
}: PieceProps) => {
	const tiltRef = useRef(null);
	const [tilt, setTilt] = useState([0, 0]);
	const tiltStyle = useSpring({ tilt });

	const [playRotateSfx] = useSound(rotateSfx);
	const [playTakeSfx] = useSound(takeSfx);
	const [playDropSfx] = useSound(dropSfx);
	const [playInsertSfx] = useSound(insertSfx);

	// const renderNum = useRenderNum('Piece');
	const [position, setPosition] = useState(() => {
		const marginY = (windowDim.height - puzzleSize.y * pieceHeight) / 2;
		const marginX = (windowDim.width - puzzleSize.x * pieceWidth) / 2;
		if (marginY > pieceHeight) {
			const deltaY = Math.random() * (marginY / pieceHeight - 1);
			return {
				x: (puzzleSize.x + 1) * Math.random() - 1,
				y: i < (puzzleSize.x * puzzleSize.y) / 2 ? -1 - deltaY : puzzleSize.y + deltaY,
			};
		} else if (marginX > pieceWidth) {
			const deltaX = Math.random() * (marginX / pieceWidth - 1);
			return {
				x: i < (puzzleSize.x * puzzleSize.y) / 2 ? -1 - deltaX : puzzleSize.x + deltaX,
				y: (puzzleSize.y + 1) * Math.random() - 1,
			};
		} else {
			return {
				x: puzzleSize.x * Math.random() - 0.5,
				y: puzzleSize.y * Math.random() - 0.5,
			};
		}
	});
	const [rotation, setRotation] = useState(Math.random() < 0.25 ? 0 : Math.trunc(4 * Math.random())); // 25+19= 44% rotation is correct
	const rotateStyle = useSpring({ rotateZ: rotation * 90 + 'deg', config: config.stiff });
	const calcTilt = useCallback(
		(x: number, y: number, rect: any) => [-(y - rect.top - rect.height / 2) / pieceWidth, (x - rect.left - rect.width / 2) / pieceHeight],
		[pieceWidth, pieceHeight]
	);
	const tiltTransform = useCallback(
		(x: number, y: number) => {
			const rotX = 60 * (rotation % 4 === 0 ? x : rotation % 4 === 1 ? y : rotation % 4 === 2 ? -x : -y);
			const rotY = 60 * (rotation % 4 === 0 ? y : rotation % 4 === 1 ? -x : rotation % 4 === 2 ? -y : x);
			const limit = (value: number) => Math.max(-30, Math.min(30, value));
			return `perspective(600px) rotateX(${limit(rotX)}deg) rotateY(${limit(rotY)}deg)`;
		},
		[rotation]
	);
	const [zIndexStyle, zIndexApi] = useSpring(() => ({ zIndex: 1000 }));
	const [scaleStyle, scaleApi] = useSpring(() => ({ scale: 1 }));
	const [dragStyle, dragApi] = useSpring(() => ({
		x: (puzzleSize.x / 2 - 0.5) * pieceWidth,
		y: (puzzleSize.y / 2 - 0.5) * pieceHeight,
	}));
	const [shadowStyle, shadowApi] = useSpring(() => ({ opacity: 0 }));

	useEffect(() => {
		if (visible) {
			const leftBound = -(windowDim.width - pieceWidth * puzzleSize.x) / 2 - pieceWidth / 2;
			const rightBound = pieceWidth * (puzzleSize.x - 1) - leftBound;
			const topBound = -(windowDim.height - pieceHeight * puzzleSize.y) / 2 - pieceHeight / 2;
			const bottomBound = pieceHeight * (puzzleSize.y - 1) - topBound;
			if (position.x * pieceWidth < leftBound) {
				setPosition({ x: leftBound / pieceWidth, y: position.y });
			} else if (position.x * pieceWidth > rightBound) {
				setPosition({ x: rightBound / pieceWidth, y: position.y });
			} else if (position.y * pieceHeight < topBound) {
				setPosition({ x: position.x, y: topBound / pieceHeight });
			} else if (position.y * pieceHeight > bottomBound) {
				setPosition({ x: position.x, y: bottomBound / pieceHeight });
			} else {
				dragApi.start({
					x: position.x * pieceWidth,
					y: position.y * pieceHeight,
					config: { ...config.default, duration: 200 },
				});
				scaleApi.start({ scale: 1, config: { ...config.default, duration: 200 } });
				shadowApi.start({ opacity: 0 });
			}
		}
	}, [position, pieceWidth, pieceHeight, puzzleSize, windowDim, dragApi, scaleApi, shadowApi, visible]);

	const useDragGesture = createUseGesture([dragAction]);

	const bindDrag = useDragGesture(
		{
			onDragStart: () => {
				scaleApi.start({ scale: 1.15, config: { ...config.wobbly, duration: 100 } });
				shadowApi.start({ opacity: 0.5, config: { duration: 100 } });
				zIndexApi.start({ zIndex: zIndexMax.current + 1000, immediate: true });
				zIndexMax.current++;
				if (
					position.x % 1 === 0 &&
					position.x >= 0 &&
					position.x < puzzleSize.x &&
					position.y % 1 === 0 &&
					position.y >= 0 &&
					position.y < puzzleSize.y
				) {
					emptyCell(position.x + position.y * puzzleSize.x);
				}
				playTakeSfx();
			},
			onDragEnd: ({ movement: [x, y], tap }) => {
				if (!tap) {
					setTilt([0, 0]);
					const gridX = position.x + x / pieceWidth;
					const gridY = position.y + y / pieceHeight;
					const cellX = Math.round(gridX);
					const cellY = Math.round(gridY);
					if (
						(gridX - cellX) * (gridX - cellX) + (gridY - cellY) * (gridY - cellY) < 0.25 * 0.25 &&
						cellX >= 0 &&
						cellX < puzzleSize.x &&
						cellY >= 0 &&
						cellY < puzzleSize.y &&
						fillCell(cellX + cellY * puzzleSize.x, rotation % 4 === 0 && id === cellX + cellY * puzzleSize.x)
					) {
						zIndexApi.start({ zIndex: zIndexMax.current, immediate: true });
						setPosition({ x: cellX, y: cellY });
						playInsertSfx();
					} else {
						setPosition({ x: gridX, y: gridY });
						playDropSfx();
					}
				}
			},
			onDrag: ({ movement: [x, y], initial: [ix, iy], tap, active }) => {
				if (tap) {
					const pid = position.x + position.y * puzzleSize.x;
					if ((rotation + 1) % 4 === 0 && id === pid) {
						emptyCell(pid);
						fillCell(pid, true);
					}
					setRotation((rot) => rot + 1);
					playRotateSfx();
				}
				if (active) {
					dragApi.start({
						x: position.x * pieceWidth + x,
						y: position.y * pieceHeight + y,
						config: config.stiff,
					});
					const leftMargin = (windowDim.width - pieceWidth * puzzleSize.x) / 2;
					const topMargin = (windowDim.height - pieceHeight * puzzleSize.y) / 2;
					const deltaX = 0.5 * (ix - leftMargin - (position.x + 0.5) * pieceWidth);
					const deltaY = 0.5 * (iy - topMargin - (position.y + 0.5) * pieceHeight);
					const tiltRect = (tiltRef.current as any).getBoundingClientRect();
					setTilt(
						calcTilt(leftMargin + (position.x + 0.5) * pieceWidth + x - deltaX, topMargin + (position.y + 0.5) * pieceHeight + y - deltaY, tiltRect)
					);
				}
			},
			// onWheelStart: ({ movement: [_, y] }) => {
			// 	setRotation((rot) => (y > 0 ? rot + 1 : rot - 1));
			// },
		},
		{
			drag: { filterTaps: true, preventDefault: true, enabled },
		}
	);

	return (
		<animated.div
			{...bindDrag()}
			className="absolute touch-none select-none"
			style={{
				...dragStyle,
				...rotateStyle,
				...zIndexStyle,
				...scaleStyle,
				width: pieceWidth,
				height: pieceHeight,
				visibility: visible ? 'visible' : 'hidden',
			}}>
			<div
				ref={tiltRef}
				className="pointer-events-none absolute select-none"
				style={{
					width: pieceWidth / (1 - 2 * overlap),
					height: pieceHeight / (1 - 2 * overlap),
					left: -pieceWidth / (1 / overlap - 2),
					top: -pieceHeight / (1 / overlap - 2),
				}}>
				{visible && (
					<animated.img
						src={image}
						alt="piece"
						className="absolute h-full w-full select-none blur-sm brightness-0 md:blur"
						style={{
							...shadowStyle,
							display: shadowStyle.opacity.to((op) => (op === 0 ? 'none' : 'block')) as any,
							transform: tiltStyle.tilt.to(tiltTransform),
						}}
					/>
				)}
				<animated.img
					src={image}
					alt="piece"
					className="absolute h-full w-full select-none"
					style={{ transform: tiltStyle.tilt.to(tiltTransform) }}
					onLoad={() => onLoadImage(id)}
					onError={onLoadError}
				/>
			</div>
		</animated.div>
	);
};

export default Piece;
